import React, { useState, useRef } from "react"
import { XMarkIcon } from "@heroicons/react/20/solid"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

interface PixelIdBadgesProps {
  pixelIds: string[]
  onPixelIdsChange: (pixelIds: string[]) => void
  platform: string
  className?: string
}

export const PixelIdBadges: React.FC<PixelIdBadgesProps> = ({
  pixelIds,
  onPixelIdsChange,
  platform,
  className,
}) => {
  const [inputValue, setInputValue] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)

  const addPixelId = () => {
    if (inputValue.trim()) {
      const updatedPixelIds = [...pixelIds, inputValue.trim()]
      onPixelIdsChange(updatedPixelIds)
      setInputValue("")
    }
  }

  const removePixelId = (index: number) => {
    const updatedPixelIds = pixelIds.filter((_, i) => i !== index)
    onPixelIdsChange(updatedPixelIds)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      addPixelId()
    }
    if (e.key === "Backspace" && inputValue === "" && pixelIds.length > 0) {
      removePixelId(pixelIds.length - 1)
    }
  }

  const handleContainerClick = () => {
    inputRef.current?.focus()
  }

  const filteredPixelIds = (pixelIds || []).filter(id => id && id.trim() !== "")

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center justify-between">
        <h6 className="text-sm font-semibold text-gray-700">Pixel IDs</h6>
      </div>

      <div
        className="min-h-[44px] w-full px-3 py-2 rounded-lg border border-basic-blue bg-basic-dark-blue cursor-text focus-within:ring-2 focus-within:ring-transparent focus-within:border-basic-blue transition-colors"
        onClick={handleContainerClick}
      >
        <div className="flex flex-wrap items-center justify-center gap-2 min-h-[28px]">
          {filteredPixelIds.map((pixelId, index) => (
            <div
              key={index}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-system-blue/20 text-system-blue text-sm rounded border border-system-blue/30"
            >
              <span className="font-semibold">{pixelId}</span>
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  removePixelId(index)
                }}
                className="ml-1 p-0.5 hover:bg-system-blue/30 rounded transition-colors"
                title="Remove pixel ID"
              >
                <XMarkIcon className="w-3 h-3" />
              </button>
            </div>
          ))}

          <input
            ref={inputRef}
            type="text"
            placeholder={
              filteredPixelIds.length === 0
                ? `Enter ${platform} Pixel IDs and press Enter`
                : ""
            }
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 min-w-[120px] p-0 leading-4 text-sm bg-transparent font-semibold outline-none focus:outline-none focus:ring-0 placeholder:text-gray-400"
          />
        </div>
      </div>

      <div className="text-xs text-gray-500">
        Type a pixel ID and press Enter to add. Click on badges to remove them.
      </div>
    </div>
  )
}
